import { useState, useEffect } from 'react';
import { Upload, Scan, Search, CheckCircle2, Loader2 } from 'lucide-react';

/**
 * FaceSearchProgress Component - 2026 UX/UI Design
 * Features:
 * - 4-stage horizontal progress (Upload → Analyze → Search → Complete)
 * - Animated icons per stage
 * - Inline component (replaces modal overlay)
 * - Smooth transitions between stages
 */

const STAGES = [
  { id: 'upload', label: 'Upload', icon: Upload, description: 'Uploading photo...' },
  { id: 'analyze', label: 'Analyze', icon: Scan, description: 'Analyzing facial features...' },
  { id: 'search', label: 'Search', icon: Search, description: 'Searching gallery...' },
  { id: 'complete', label: 'Complete', icon: CheckCircle2, description: 'Search complete!' },
];

export function FaceSearchProgress({
  currentStage = 'upload', // 'upload' | 'analyze' | 'search' | 'complete'
  progress = 0, // 0-100 for overall progress
  isSearching = false,
  resultCount = 0,
  className = '',
}) {
  const [animatedStage, setAnimatedStage] = useState(0);
  const currentStageIndex = STAGES.findIndex(s => s.id === currentStage);

  // Animate stage progression
  useEffect(() => {
    if (currentStageIndex > animatedStage) {
      const timer = setTimeout(() => {
        setAnimatedStage(currentStageIndex);
      }, 300);
      return () => clearTimeout(timer);
    }
    setAnimatedStage(currentStageIndex);
  }, [currentStageIndex, animatedStage]);

  if (!isSearching && currentStage !== 'complete') {
    return null;
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Progress Container */}
      <div className="
        bg-gradient-to-br from-purple-50 to-pink-50
        dark:from-purple-900/20 dark:to-pink-900/20
        rounded-2xl border-2 border-purple-200 dark:border-purple-800/50
        p-6 shadow-lg
      ">
        {/* Stage Indicators */}
        <div className="flex items-center justify-between mb-6">
          {STAGES.map((stage, index) => {
            const Icon = stage.icon;
            const isActive = index === currentStageIndex;
            const isComplete = index < currentStageIndex;
            const isPending = index > currentStageIndex;

            return (
              <div key={stage.id} className="flex flex-col items-center flex-1">
                {/* Stage Circle */}
                <div className="relative">
                  <div
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      transition-all duration-500 ease-out
                      ${isComplete
                        ? 'bg-gradient-to-br from-green-400 to-emerald-500 text-white shadow-lg shadow-green-500/30 animate-step-complete'
                        : isActive
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-400'
                      }
                    `}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="w-6 h-6" />
                    ) : isActive ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Icon className="w-6 h-6" />
                    )}
                  </div>

                  {/* Pulse ring for active stage */}
                  {isActive && (
                    <div className="
                      absolute inset-0 rounded-full
                      bg-purple-500/30
                      animate-ping
                    " />
                  )}
                </div>

                {/* Stage Label */}
                <span
                  className={`
                    mt-2 text-xs font-semibold
                    transition-colors duration-300
                    ${isComplete
                      ? 'text-green-600 dark:text-green-400'
                      : isActive
                      ? 'text-purple-600 dark:text-purple-400'
                      : 'text-slate-400'
                    }
                  `}
                >
                  {stage.label}
                </span>

                {/* Connector Line (except last) */}
                {index < STAGES.length - 1 && (
                  <div
                    className={`
                      absolute top-6 left-1/2 w-full h-0.5
                      ${index < currentStageIndex
                        ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                        : 'bg-slate-200 dark:bg-slate-700'
                      }
                      transition-all duration-500
                    `}
                    style={{
                      transform: 'translateY(-50%)',
                      zIndex: -1,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="relative h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
          <div
            className="
              absolute inset-y-0 left-0
              bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500
              rounded-full
              transition-all duration-500 ease-out
            "
            style={{
              width: `${progress}%`,
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.5s infinite',
            }}
          />
        </div>

        {/* Current Stage Description */}
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {currentStage === 'complete' ? (
              <span className="text-green-600 dark:text-green-400">
                Found {resultCount} matching photo{resultCount !== 1 ? 's' : ''}!
              </span>
            ) : (
              STAGES[currentStageIndex]?.description
            )}
          </p>
        </div>

        {/* Animated Dots */}
        {isSearching && currentStage !== 'complete' && (
          <div className="flex justify-center gap-1 mt-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full bg-purple-400 dark:bg-purple-500"
                style={{
                  animation: 'float 1s ease-in-out infinite',
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FaceSearchProgress;
